import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      const token = searchParams.get('token');
      const refreshToken = searchParams.get('refreshToken');
      const errorParam = searchParams.get('error');

      console.log('🔐 AuthCallback: Starting...', { 
        hasToken: !!token, 
        hasRefreshToken: !!refreshToken, 
        errorParam,
        origin: window.location.origin 
      });

      if (errorParam) {
        console.log('❌ AuthCallback: Error param received:', errorParam);
        setStatus('error');
        setError(getErrorMessage(errorParam));
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (token && refreshToken) {
        try {
          // Store tokens with correct keys that AuthContext expects
          console.log('💾 AuthCallback: Storing tokens in localStorage...');
          localStorage.setItem('pilotleague_token', token);
          localStorage.setItem('pilotleague_refresh_token', refreshToken);

          // Fetch user profile - use relative URL for production
          const apiUrl = `${window.location.origin}/api/auth/me`;
          console.log('📡 AuthCallback: Fetching user profile from:', apiUrl);
          
          const response = await fetch(apiUrl, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          console.log('📡 AuthCallback: Response status:', response.status);

          if (response.ok) {
            const data = await response.json();
            console.log('✅ AuthCallback: User data received:', data);
            // Le backend renvoie directement les données user, pas { user: ... }
            localStorage.setItem('user', JSON.stringify(data));
            
            setStatus('success');
            setTimeout(() => {
              window.location.href = '/';
            }, 1500);
          } else {
            const errorText = await response.text();
            console.error('❌ AuthCallback: Response not OK:', response.status, errorText);
            throw new Error(`Failed to fetch user profile: ${response.status} - ${errorText}`);
          }
        } catch (err) {
          console.error('❌ AuthCallback: Exception caught:', err);
          setStatus('error');
          setError(`Erreur: ${err.message}`);
          setTimeout(() => navigate('/login'), 5000);
        }
      } else {
        console.log('❌ AuthCallback: Missing tokens');
        setStatus('error');
        setError('Tokens manquants dans la réponse.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, login]);

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'google_auth_failed':
        return 'L\'authentification Google a échoué.';
      case 'google_token_failed':
        return 'Impossible d\'obtenir le token Google.';
      case 'google_no_email':
        return 'Aucun email associé au compte Google.';
      case 'google_auth_error':
        return 'Erreur lors de l\'authentification Google.';
      default:
        return 'Une erreur est survenue.';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center">
      <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-gray-700/50 text-center max-w-md w-full mx-4">
        {status === 'processing' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 relative">
              <div className="absolute inset-0 border-4 border-blue-500/30 rounded-full animate-ping"></div>
              <div className="absolute inset-0 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
              <div className="absolute inset-2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Connexion en cours...</h2>
            <p className="text-gray-400">Veuillez patienter pendant que nous finalisons votre connexion.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Connexion réussie !</h2>
            <p className="text-gray-400">Vous allez être redirigé vers votre tableau de bord.</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-red-500 to-rose-500 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Erreur de connexion</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <p className="text-gray-500 text-sm">Redirection vers la page de connexion...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallbackPage;
